import React, { useState } from 'react';
import { useDialog } from '../context/DialogContext';
import { useLocale } from '../context/LocaleContext';
import { progressService } from '../services/progressService';
import { formatDateWithRelative, calculateDuration } from '../utils/dateUtils';
import styles from '../styles/historyModal.module.css';

interface HistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepNumber: number;
  stepTitle: string;
  stepData: any;
  onStepUpdated?: () => void;
}

interface TimelineItem {
  date: string;
  action: string;
  kind: 'started' | 'completed';
  isStepEvent: boolean;
  isInProgress?: boolean;
}

const HistoryModal: React.FC<HistoryModalProps> = ({
  isOpen,
  onClose,
  stepNumber,
  stepTitle,
  stepData,
  onStepUpdated
}) => {
  const { showConfirm, showAlert } = useDialog();
  const { t } = useLocale();
  const [isEditing, setIsEditing] = useState(false);
  const [newStartDate, setNewStartDate] = useState('');
  const [newCompletedDate, setNewCompletedDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  if (!isOpen) return null;

  // Build timeline from step data
  const buildTimeline = (): TimelineItem[] => {
    const timeline: TimelineItem[] = [];
    
    // Add step start
    if (stepData.started_at) {
      timeline.push({
        date: stepData.started_at,
        action: t('history.timeline.started', { stepTitle }),
        kind: 'started',
        isStepEvent: true
      });
    }
    
    // Add step completion - show even if not completed yet (with "In progress")
    if (stepData.started_at) {
      if (stepData.completed && stepData.completed_at) {
        timeline.push({
          date: stepData.completed_at,
          action: t('history.timeline.completed', { stepTitle }),
          kind: 'completed',
          isStepEvent: true
        });
      } else {
        // Step is started but not completed - show "In progress"
        // Use current date for sorting so it appears after the start date
        timeline.push({
          date: new Date().toISOString(), // Use current date for sorting
          action: t('history.timeline.completed', { stepTitle }),
          kind: 'completed',
          isStepEvent: true,
          isInProgress: true // Flag to indicate this is an in-progress item
        });
      }
    }
    
    // Sort by date
    return timeline.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  };

  const timeline = buildTimeline();
  const duration = stepData.started_at && stepData.completed_at 
    ? calculateDuration(stepData.started_at, stepData.completed_at)
    : stepData.started_at 
    ? calculateDuration(stepData.started_at, new Date().toISOString())
    : 0;

  const handleEditDates = () => {
    setIsEditing(true);
    setNewStartDate(stepData.started_at ? stepData.started_at.split('T')[0] : '');
    setNewCompletedDate(stepData.completed_at ? stepData.completed_at.split('T')[0] : '');
  };

  const handleSaveDates = async () => {
    if (!newStartDate) {
      showAlert({
        message: t('history.alert.start_date_required'),
        variant: 'warning'
      });
      return;
    }

    // Validate start date
    const startDate = new Date(newStartDate);
    if (isNaN(startDate.getTime())) {
      showAlert({
        message: t('history.alert.start_date_invalid'),
        variant: 'warning'
      });
      return;
    }

    if (startDate > new Date()) {
      showAlert({
        message: t('history.alert.start_date_future'),
        variant: 'warning'
      });
      return;
    }

    // Validate completion date if provided
    if (newCompletedDate) {
      const completedDate = new Date(newCompletedDate);
      if (isNaN(completedDate.getTime())) {
        showAlert({
          message: t('history.alert.completed_date_invalid'),
          variant: 'warning'
        });
        return;
      }

      if (completedDate > new Date()) {
        showAlert({
          message: t('history.alert.completed_date_future'),
          variant: 'warning'
        });
        return;
      }

      if (completedDate < startDate) {
        showAlert({
          message: t('history.alert.completed_before_start'),
          variant: 'warning'
        });
        return;
      }
    }

    showConfirm({
      message: t('history.edit_dates_title'),
      onConfirm: async () => {
        setIsLoading(true);
        try {
          const updateData: any = { started_at: new Date(newStartDate).toISOString() };
          if (newCompletedDate) {
            updateData.completed_at = new Date(newCompletedDate).toISOString();
          }
          
          await progressService.updateStepDates(stepNumber, updateData);
          
          showAlert({
            message: t('history.alert.dates_updated'),
            variant: 'success'
          });
          
          setIsEditing(false);
          onStepUpdated?.();
        } catch (error) {
          console.error('Error updating dates:', error);
          showAlert({
            message: error instanceof Error ? error.message : t('history.alert.update_failed'),
            variant: 'danger'
          });
        } finally {
          setIsLoading(false);
        }
      },
      variant: 'info'
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setNewStartDate('');
    setNewCompletedDate('');
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3>{t('history.title', { stepTitle })}</h3>
          <button className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>
        
        <div className={styles.content}>
          {timeline.length === 0 ? (
            <p className={styles.noData}>{t('history.empty')}</p>
          ) : (
            <>
              <div className={styles.timeline}>
                {timeline.map((item, index) => (
                  <div key={index} className={`${styles.timelineItem} ${item.isStepEvent ? styles.stepEvent : styles.actionEvent}`}>
                    <span className={styles.action}>
                      {item.isStepEvent ? item.action : `- ${item.action}`}
                    </span>
                    <span className={styles.date}>
                      {item.isInProgress 
                        ? t('history.in_progress') 
                        : formatDateWithRelative(item.date)}
                    </span>
                    <span className={styles.editColumn}>
                      {item.isStepEvent && (item.kind === 'started' || item.kind === 'completed') && !item.isInProgress && (
                        <button 
                          className={styles.inlineEditButton}
                          onClick={handleEditDates}
                          disabled={isLoading}
                          title={t('history.edit_dates_tooltip')}
                        >
                          {t('history.edit_button')}
                        </button>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              
              {duration > 0 && (
                <div className={styles.duration}>
                  {duration === 1
                    ? t('history.duration_one', { count: duration })
                    : t('history.duration_other', { count: duration })}
                </div>
              )}
            </>
          )}
        </div>
        
        {isEditing && (
          <div className={styles.editModal}>
            <div className={styles.editContent}>
              <h4>{t('history.edit_dates_title')}</h4>
              <div className={styles.dateInput}>
                <label htmlFor="startDate">{t('history.start_date_label')}</label>
                <input
                  id="startDate"
                  type="date"
                  value={newStartDate}
                  onChange={(e) => setNewStartDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              {stepData.completed && (
                <div className={styles.dateInput}>
                  <label htmlFor="completedDate">{t('history.completed_date_label')}</label>
                  <input
                    id="completedDate"
                    type="date"
                    value={newCompletedDate}
                    onChange={(e) => setNewCompletedDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    min={newStartDate}
                  />
                </div>
              )}
              <div className={styles.editActions}>
                <button 
                  className={styles.saveButton}
                  onClick={handleSaveDates}
                  disabled={isLoading}
                >
                  {isLoading ? t('history.saving') : t('dialog.save')}
                </button>
                <button 
                  className={styles.cancelButton}
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                >
                  {t('dialog.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryModal; 